/**
 * Nova Derma — attachment upload (INV-14 supplier contracts, INV-71 multi-file
 * purchase invoices, PAT-25 photo intake, LAB-11 result scans).
 *
 * The drop zone validates before it hands anything to the caller: a rejected
 * type or an oversized file never reaches the network, and the reason is shown
 * in Arabic under the zone instead of as a toast that disappears.
 */

import { forwardRef, useCallback, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent as ReactDragEvent, HTMLAttributes, ReactNode } from 'react';
import {
  AlertCircle,
  ExternalLink,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLang } from '../../i18n';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** A file that already lives in the files module and has a signed URL. */
export interface UploadedAttachment {
  id: string;
  /** File name as the user stored it. */
  name: string;
  /** Signed URL from `FilesModule` — opened in a new tab, never proxied. */
  url: string;
  sizeBytes?: number | null;
  mimeType?: string | null;
  /** ISO instant. */
  uploadedAt?: string | null;
}

/**
 * Reported by the caller from axios `onUploadProgress`. Without it the row still
 * shows a live spinner — the bar simply stays at zero rather than inventing a
 * number.
 */
export type UploadProgressReporter = (fileIndex: number, percent: number) => void;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Split so the digits keep `font-mono ltr-num` and the unit stays text (DS-51). */
function fileSizeParts(bytes: number, units: readonly string[]): [string, string] {
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const decimals = unit === 0 || value >= 100 ? 0 : 1;
  return [value.toFixed(decimals), units[unit] ?? ''];
}

/** Mirrors the browser's own `accept` matching so the message can be ours. */
function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const tokens = accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;

  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic', 'heif', 'bmp'];
const SHEET_EXT = ['xlsx', 'xls', 'csv'];

function iconFor(name: string, mimeType?: string | null): { Icon: LucideIcon; tone: string } {
  const mt = (mimeType ?? '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';

  if (mt.startsWith('image/') || IMAGE_EXT.includes(ext)) return { Icon: ImageIcon, tone: 'text-purple-500' };
  if (mt === 'application/pdf' || ext === 'pdf') return { Icon: FileText, tone: 'text-red-500' };
  if (mt.includes('spreadsheet') || mt.includes('excel') || SHEET_EXT.includes(ext)) {
    return { Icon: FileSpreadsheet, tone: 'text-emerald-600' };
  }
  return { Icon: FileIcon, tone: 'text-slate-400' };
}

/** The API ships both messages; anything else gets the generic one. */
function uploadErrorMessage(error: unknown, lang: 'AR' | 'EN', t: TFunction<'common'>): string {
  const body = (
    error as { response?: { data?: { messageAr?: string; messageEn?: string } } } | null | undefined
  )?.response?.data;
  const localized = lang === 'EN' ? (body?.messageEn ?? body?.messageAr) : body?.messageAr;
  return localized ?? t('ui.upload.failed');
}

function acceptSummary(accept?: string): string {
  if (!accept) return '';
  return accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => (t.startsWith('.') ? t.slice(1) : t.replace('/*', '')))
    .join(' / ')
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// AttachmentList
// ---------------------------------------------------------------------------

export interface AttachmentListProps extends Omit<HTMLAttributes<HTMLUListElement>, 'children'> {
  items: UploadedAttachment[];
  /** Omit to render a read-only list (a posted invoice keeps its attachments). */
  onRemove?: (attachment: UploadedAttachment) => void;
  emptyLabel?: string;
  openLabel?: string;
  removeLabel?: string;
}

export const AttachmentList = forwardRef<HTMLUListElement, AttachmentListProps>(function AttachmentList(
  {
    items,
    onRemove,
    emptyLabel: emptyLabelProp,
    openLabel: openLabelProp,
    removeLabel: removeLabelProp,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const emptyLabel = emptyLabelProp ?? t('ui.upload.empty');
  const openLabel = openLabelProp ?? t('ui.upload.open');
  const removeLabel = removeLabelProp ?? t('ui.upload.remove');
  const sizeUnits = t('ui.upload.sizeUnits', { returnObjects: true }) as string[];

  return (
    <ul ref={ref} className={cn('rounded border border-slate-200 bg-white', className)} {...rest}>
      {items.length === 0 ? (
        <li className="px-2 py-2 text-center text-[11px] text-slate-400">{emptyLabel}</li>
      ) : (
        items.map((item) => {
          const { Icon, tone } = iconFor(item.name, item.mimeType);
          const size =
            typeof item.sizeBytes === 'number' && item.sizeBytes >= 0
              ? fileSizeParts(item.sizeBytes, sizeUnits)
              : null;

          return (
            <li
              key={item.id}
              className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5 last:border-b-0 hover:bg-slate-50"
            >
              <Icon className={cn('h-4 w-4 shrink-0', tone)} aria-hidden="true" />

              <span className="selectable min-w-0 flex-1 truncate text-xs text-slate-700" title={item.name}>
                {item.name}
              </span>

              {size ? (
                <span className="shrink-0 text-[10px] text-slate-500">
                  <span className="font-mono tabular-nums ltr-num">{size[0]}</span> {size[1]}
                </span>
              ) : null}

              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                title={openLabel}
                className="btn-ghost h-6 w-6 shrink-0 p-0 text-blue-600 hover:bg-blue-50"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{openLabel}</span>
              </a>

              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(item)}
                  title={removeLabel}
                  className="btn-ghost h-6 w-6 shrink-0 p-0 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">{removeLabel}</span>
                </button>
              ) : null}
            </li>
          );
        })
      )}
    </ul>
  );
});

// ---------------------------------------------------------------------------
// FileUpload
// ---------------------------------------------------------------------------

interface QueueItem {
  key: string;
  name: string;
  sizeBytes: number;
  percent: number;
  failed: boolean;
  error?: string;
}

export interface FileUploadProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onError'> {
  /** Attachments already stored on the record; rendered under the drop zone. */
  value?: UploadedAttachment[];
  /**
   * Resolve once the batch is stored. Reject with the API error and the rows stay
   * on screen carrying `messageAr`, so nothing is lost silently.
   */
  onUpload: (files: File[], onProgress: UploadProgressReporter) => Promise<unknown>;
  /** Wire this up to expose the delete button on the existing attachments. */
  onRemove?: (attachment: UploadedAttachment) => void;
  /** Standard HTML accept list — `.pdf,image/*`. */
  accept?: string;
  multiple?: boolean;
  maxSizeMb?: number;
  disabled?: boolean;
  /** Replaces the generated constraints line. */
  hint?: ReactNode;
  dropLabel?: string;
  browseLabel?: string;
  emptyLabel?: string;
}

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(function FileUpload(
  {
    value = [],
    onUpload,
    onRemove,
    accept,
    multiple = false,
    maxSizeMb = 25,
    disabled = false,
    hint,
    dropLabel: dropLabelProp,
    browseLabel: browseLabelProp,
    emptyLabel: emptyLabelProp,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const lang = useLang();
  const dropLabel = dropLabelProp ?? t('ui.upload.drop');
  const browseLabel = browseLabelProp ?? t('ui.upload.browse');
  const emptyLabel = emptyLabelProp ?? t('ui.upload.empty');
  const sizeUnits = t('ui.upload.sizeUnits', { returnObjects: true }) as string[];

  const inputRef = useRef<HTMLInputElement | null>(null);
  // dragenter/dragleave fire for every child, so the zone counts depth instead
  // of flickering on the way past the icon.
  const dragDepth = useRef(0);
  const batchSeq = useRef(0);

  const [dragging, setDragging] = useState(false);
  const [rejections, setRejections] = useState<string[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleFiles = useCallback(
    async (incoming: File[]) => {
      if (disabled || incoming.length === 0) return;

      const picked = multiple ? incoming : incoming.slice(0, 1);
      const limitBytes = maxSizeMb * 1024 * 1024;
      const rejected: string[] = [];
      const accepted: File[] = [];

      for (const file of picked) {
        if (!matchesAccept(file, accept)) {
          rejected.push(t('ui.upload.rejectedType', { name: file.name }));
          continue;
        }
        if (file.size > limitBytes) {
          const [amount, unit] = fileSizeParts(file.size, sizeUnits);
          rejected.push(t('ui.upload.rejectedSize', { name: file.name, amount, unit }));
          continue;
        }
        accepted.push(file);
      }

      setRejections(rejected);
      if (accepted.length === 0) return;

      batchSeq.current += 1;
      const batch = batchSeq.current;
      const keys = accepted.map((_, index) => `${batch}-${index}`);
      const rows: QueueItem[] = accepted.map((file, index) => ({
        key: keys[index],
        name: file.name,
        sizeBytes: file.size,
        percent: 0,
        failed: false,
      }));

      setQueue((current) => [...current, ...rows]);

      const report: UploadProgressReporter = (fileIndex, percent) => {
        const key = keys[fileIndex];
        if (!key) return;
        const safe = Math.min(100, Math.max(0, Math.round(percent)));
        setQueue((current) => current.map((row) => (row.key === key ? { ...row, percent: safe } : row)));
      };

      try {
        await onUpload(accepted, report);
        setQueue((current) => current.filter((row) => !keys.includes(row.key)));
      } catch (error) {
        const message = uploadErrorMessage(error, lang, t);
        setQueue((current) =>
          current.map((row) => (keys.includes(row.key) ? { ...row, failed: true, error: message } : row)),
        );
      }
    },
    [accept, disabled, maxSizeMb, multiple, onUpload, t, lang, sizeUnits],
  );

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      // Reset so picking the same file twice in a row still fires a change.
      event.target.value = '';
      void handleFiles(files);
    },
    [handleFiles],
  );

  const onDragEnter = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (disabled) return;
      dragDepth.current += 1;
      setDragging(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!disabled) event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDragLeave = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (disabled) return;
      const files = event.dataTransfer.files ? Array.from(event.dataTransfer.files) : [];
      void handleFiles(files);
    },
    [disabled, handleFiles],
  );

  const constraints = acceptSummary(accept);

  return (
    <div ref={ref} data-testid="file-upload" className={cn('flex flex-col gap-2', className)} {...rest}>
      <div
        data-testid="file-upload-dropzone"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={openPicker}
        aria-disabled={disabled}
        className={cn(
          'flex flex-col items-center justify-center gap-1.5 rounded border-2 border-dashed p-4 text-center transition',
          dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50/60',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-slate-400',
        )}
      >
        <Upload className={cn('h-5 w-5', dragging ? 'text-blue-500' : 'text-slate-400')} aria-hidden="true" />

        <p className="text-xs font-bold text-slate-600">{dropLabel}</p>

        <button
          type="button"
          data-testid="file-upload-browse"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            openPicker();
          }}
          className="btn-secondary"
        >
          <Paperclip className="h-4 w-4" aria-hidden="true" />
          {browseLabel}
        </button>

        <p className="text-[10px] text-slate-500">
          {hint ?? (
            <>
              {/* DS-51 — the limit stays LTR and monospaced inside either sentence. */}
              <Trans
                i18nKey="ui.upload.maxSize"
                ns="common"
                values={{ mb: maxSizeMb }}
                components={[<span className="font-mono tabular-nums ltr-num" key="mb" />]}
              />
              {constraints ? (
                <>
                  {' · '}
                  <span className="font-mono ltr-num">{constraints}</span>
                </>
              ) : null}
              {multiple ? t('ui.upload.multipleHint') : null}
            </>
          )}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        data-testid="file-upload-input"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={onInputChange}
      />

      {rejections.length > 0 ? (
        <ul className="rounded border border-red-200 bg-red-50 px-2 py-1.5">
          {rejections.map((message) => (
            <li key={message} className="flex items-start gap-1.5 text-[11px] text-red-700">
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {queue.length > 0 ? (
        <ul className="rounded border border-slate-200 bg-white">
          {queue.map((row) => {
            const [amount, unit] = fileSizeParts(row.sizeBytes, sizeUnits);
            return (
              <li key={row.key} className="border-b border-slate-100 px-2 py-1.5 last:border-b-0">
                <div className="flex items-center gap-2">
                  {row.failed ? (
                    <AlertCircle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                  ) : (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-600" aria-hidden="true" />
                  )}

                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700" title={row.name}>
                    {row.name}
                  </span>

                  <span className="shrink-0 text-[10px] text-slate-500">
                    <span className="font-mono tabular-nums ltr-num">{amount}</span> {unit}
                  </span>

                  {row.failed ? (
                    <button
                      type="button"
                      title={t('ui.upload.removeFromList')}
                      onClick={() => setQueue((current) => current.filter((it) => it.key !== row.key))}
                      className="btn-ghost h-6 w-6 shrink-0 p-0 text-slate-500 hover:bg-slate-100"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">{t('ui.upload.removeFromList')}</span>
                    </button>
                  ) : (
                    <span className="w-8 shrink-0 text-end font-mono text-[10px] tabular-nums text-slate-500 ltr-num">
                      {row.percent}%
                    </span>
                  )}
                </div>

                {row.failed ? (
                  <p className="mt-1 text-[11px] text-red-700">{row.error}</p>
                ) : (
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-[width] duration-200"
                      style={{ width: `${row.percent}%` }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {value.length > 0 ? <AttachmentList items={value} onRemove={onRemove} emptyLabel={emptyLabel} /> : null}
    </div>
  );
});
